import React, { useState, useEffect } from 'react';
import { Users, Award, Heart, Target, Mail, Linkedin, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../components/PageTitle';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Heart: <Heart className="w-10 h-10" />,
  Users: <Users className="w-10 h-10" />,
  Award: <Award className="w-10 h-10" />,
  Target: <Target className="w-10 h-10" />
};

const Team = () => {
  usePageTitle('Our Team | Lehigh Human Support Services');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default content (fallback)
  const defaultContent = {
    hero_title: 'Meet Our Team',
    hero_subtitle: 'Dedicated professionals committed to empowering lives and fostering community',
    values_title: 'What Makes Our Team Special',
    values_subtitle: 'Our team is the heart of everything we do',
    team_values: [
      { id: 'tv1', icon: 'Heart', title: 'Compassionate Care', description: 'Every team member is selected for their genuine compassion and dedication to serving others.' },
      { id: 'tv2', icon: 'Users', title: 'Collaborative Spirit', description: 'We work together as a unified team, supporting each other and the individuals we serve.' },
      { id: 'tv3', icon: 'Award', title: 'Professional Excellence', description: 'Our team members are highly trained, certified professionals committed to ongoing development.' },
      { id: 'tv4', icon: 'Target', title: 'Person-Centered Focus', description: 'We put the needs, goals, and preferences of individuals at the center of everything we do.' }
    ],
    leadership_title: 'Leadership Team',
    leadership_subtitle: 'Meet our experienced leaders guiding our mission and vision',
    leadership: [],
    managers_title: 'Program Managers',
    managers_subtitle: 'Dedicated professionals ensuring quality care and support',
    managers: [],
    dsp_title: 'Our Direct Support Professionals',
    dsp_description: 'The backbone of our organization, our Direct Support Professionals (DSPs) provide compassionate, person-centered care every day. Each DSP undergoes comprehensive training and brings dedication, empathy, and professionalism to their work.',
    dsp_stats: [
      { number: '50+', label: 'Trained DSPs' },
      { number: '100%', label: 'Background Checked' },
      { number: '24/7', label: 'Dedicated Support' }
    ],
    gallery_title: 'Our Team in Action',
    gallery_subtitle: 'See our team making a difference every day',
    gallery_images: [],
    cta_title: 'Join Our Amazing Team',
    cta_subtitle: 'Be part of a team that makes a real difference. Explore career opportunities with Lehigh HSS.',
    cta_button_text: 'View Career Opportunities',
    cta_button_link: '/careers'
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/team`);
      const merged = { ...defaultContent, ...response.data };
      // Ensure arrays exist
      if (!merged.team_values || merged.team_values.length === 0) {
        merged.team_values = defaultContent.team_values;
      }
      if (!merged.dsp_stats || merged.dsp_stats.length === 0) {
        merged.dsp_stats = defaultContent.dsp_stats;
      }
      setContent(merged);
    } catch (error) {
      console.error('Error fetching team content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { team_values, leadership, managers, dsp_stats, gallery_images } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-white py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-500 rounded-full mb-4">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {content.hero_title}
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              {content.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Team Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.values_title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.values_subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team_values.map((value, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-amber-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8 space-y-4 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-50 rounded-full text-amber-500">
                    {iconMap[value.icon] || <Heart className="w-10 h-10" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.leadership_title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.leadership_subtitle}
            </p>
          </div>
          {leadership && leadership.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {leadership.map((leader, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  {leader.image && (
                    <div className="h-64 overflow-hidden">
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6 space-y-3">
                    <h3 className="text-2xl font-bold text-gray-900">{leader.name}</h3>
                    <p className="text-amber-600 font-semibold">{leader.role}</p>
                    <p className="text-gray-600 leading-relaxed">{leader.bio}</p>
                    {(leader.email || leader.linkedin) && (
                      <div className="flex gap-3 pt-2">
                        {leader.email && (
                          <a href={`mailto:${leader.email}`} className="text-gray-500 hover:text-amber-500 transition-colors">
                            <Mail className="w-5 h-5" />
                          </a>
                        )}
                        {leader.linkedin && (
                          <a href={leader.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-amber-500 transition-colors">
                            <Linkedin className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>Team information will be added soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* Program Managers */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.managers_title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {content.managers_subtitle}
            </p>
          </div>
          {managers && managers.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {managers.map((manager, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  {manager.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={manager.image}
                        alt={manager.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-4 space-y-2">
                    <h3 className="text-lg font-bold text-gray-900">{manager.name}</h3>
                    <p className="text-amber-600 text-sm font-semibold">{manager.role}</p>
                    {manager.bio && (
                      <p className="text-gray-600 text-sm leading-relaxed">{manager.bio}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>Team information will be added soon.</p>
            </div>
          )}
        </div>
      </section>

      {/* DSP Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.dsp_title}</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              {content.dsp_description}
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              {dsp_stats.map((stat, index) => (
                <Card key={index} className="border-2 border-amber-200 bg-white">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl font-bold text-amber-500 mb-2">{stat.number}</div>
                    <div className="text-gray-700 font-semibold">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Gallery */}
      {gallery_images && gallery_images.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.gallery_title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {content.gallery_subtitle}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery_images.map((image, index) => (
                <div key={index} className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
                  <img
                    src={image}
                    alt={`Team photo ${index + 1}`}
                    className="w-full h-48 object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            {content.cta_title}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {content.cta_subtitle}
          </p>
          <Link to={content.cta_button_link || '/careers'}>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
              {content.cta_button_text}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Team;
